/**
 * The app's icon language, lifted verbatim from the design file (24×24 viewBox).
 * Do not redraw these.
 *
 * Two-colour glyphs: the solid body is --tb-ink-inverse, and the cut-out
 * details (bottle measure lines, note rules) are `currentColor`. Each icon sits
 * in a coloured tile that sets `color` to its own background, so the cut-outs
 * read as holes punched through the glyph regardless of tile colour.
 */
import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

const base = (size: number) => ({
  viewBox: '0 0 24 24',
  width: size,
  height: size,
  fill: 'none',
  'aria-hidden': true,
  focusable: false,
} as const)

/** Bottle — the Tiny Boss mark and the "Feeds" glyph.
 *  `detail={false}` drops the measure lines for small sizes (footer, mobile nav),
 *  matching the design file. */
export function BottleIcon({ size = 24, detail = true, ...rest }: IconProps & { detail?: boolean }) {
  return (
    <svg {...base(size)} {...rest}>
      <rect x="10" y="1.5" width="4" height="2.6" rx="1.3" fill="var(--tb-ink-inverse)" />
      <rect x="8.5" y="3.6" width="7" height="2.8" rx="1.4" fill="var(--tb-ink-inverse)" />
      <path
        d="M9 6.4h6c.2 1.1.85 1.6 1.45 2.2.55.55.85 1.2.85 2v7.5A2.5 2.5 0 0 1 14.8 20.6H9.2A2.5 2.5 0 0 1 6.7 18.1v-7.5c0-.8.3-1.45.85-2C8.15 8 8.8 7.5 9 6.4Z"
        fill="var(--tb-ink-inverse)"
      />
      {detail && (
        <>
          <rect x="9.5" y="11" width="3.4" height="1.4" rx="0.7" fill="currentColor" />
          <rect x="9.5" y="13.8" width="3.4" height="1.4" rx="0.7" fill="currentColor" />
        </>
      )}
    </svg>
  )
}

/** Moon — "Sleep". */
export function MoonIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M21 13.2A9 9 0 1 1 9.7 2.3 5.9 5.9 0 0 0 21 13.2Z" fill="var(--tb-ink-inverse)" />
    </svg>
  )
}

/** Diaper — "Diapers". */
export function DiaperIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path
        d="M12 6.2C10.8 4.4 8.2 3.8 6.3 5 4.7 6 4 8 4 10.4c0 4.4 3.6 7.6 8 7.6s8-3.2 8-7.6c0-2.4-.7-4.4-2.3-5.4-1.9-1.2-4.5-.6-5.7 1.2Z"
        fill="var(--tb-ink-inverse)"
      />
      <path d="M12 6.6V12.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

/** Note — "Notes". */
export function NoteIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path
        d="M5 5.6A2.6 2.6 0 0 1 7.6 3h6.7L19 7.6v10.8A2.6 2.6 0 0 1 16.4 21H7.6A2.6 2.6 0 0 1 5 18.4V5.6Z"
        fill="var(--tb-ink-inverse)"
      />
      <path d="M14 3.1v3.4c0 .7.6 1.3 1.3 1.3h3.4" fill="currentColor" opacity="0.5" />
      <rect x="7.7" y="11" width="8.2" height="1.6" rx="0.8" fill="currentColor" />
      <rect x="7.7" y="14" width="8.2" height="1.6" rx="0.8" fill="currentColor" />
      <rect x="7.7" y="17" width="5" height="1.6" rx="0.8" fill="currentColor" />
    </svg>
  )
}

/** Check — the subscribe success state. */
export function CheckIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke="var(--tb-ink-inverse)"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
